import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button } from 'rsuite';
import { toast } from 'react-toastify';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { isValidEmail, isValidPhone } from '../../utils/Validation';
import { adminLogin } from '../../services/apiService';
import 'rsuite/dist/rsuite.min.css';
import './Login.css';
import { setUserData } from '../../redux/slices/userDataSlice';
import { useDispatch } from 'react-redux';
import relaxwellLogo from '../../assets/relaxwell_logo.png';

const Login: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const validateForm = (username: string, password: string): { valid: boolean; message: string } => {
        if (!username && !password) {
            return { valid: false, message: 'Please enter both email or phone number and password' };
        }
        if (!username) {
            return { valid: false, message: 'Please enter your email' };
        }
        if (!isValidEmail(username) && !isValidPhone(username)) {
            return { valid: false, message: 'Please enter a valid email or phone number' };
        }
        if (!password) {
            return { valid: false, message: 'Please enter your password' };
        }
        return { valid: true, message: '' };
    };

    const handleSubmit = async (e: React.MouseEvent) => {
        e.preventDefault();
        const { valid, message } = validateForm(username, password);
        if (!valid) {
            toast.error(message);
            return;
        }

        setIsLoading(true)
        try {
            const response = await adminLogin({ username, password });

            if (response?.code === 409) {
                toast.error(response?.message, {
                    position: 'top-right',
                    autoClose: 3000,
                    pauseOnHover: true,
                })
            };

            if (response?.code === 200) {
                localStorage.setItem('accessToken', response.data.tokens.accessToken);
                localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
                dispatch(setUserData({
                    user: response.data.userDetails,
                    tokens: response.data.tokens
                }));

                toast.success(response?.message, {
                    position: 'top-center',
                    autoClose: 3000,
                    pauseOnHover: true,
                });
                navigate('/qr-generation');
            }
        } catch (error) {
            console.error('Login failed:', error);
            setIsLoading(false)
            toast.error(/* error.response?.data?.message || */ 'Something went wrong, please login again.');
        } finally {
            setIsLoading(false)
        }
    };

    return (
        <div className="login-page">
            <div className="left-section">
                <div className="logo-container">
                    <img
                        src={relaxwellLogo}
                        alt="Login Visual"
                        className="login-image"
                    />
                </div>
            </div>
            <div className="right-section">
                <div className="login-form-container">
                    <h2 className="welcome-title">Log In To Your Account</h2>
                    <p className="welcome-subtitle">Welcome back! Please enter your login details.</p>
                    <Form fluid className="login-form">
                        <Form.Group controlId="email" className="form-group">
                            <Form.ControlLabel className="form-label">
                                <EmailIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                                Email/Phone <span className="required">*</span>
                            </Form.ControlLabel>
                            <Form.Control
                                name="username"
                                type="username"
                                value={username}
                                onChange={(value: string) => setUsername(value)}
                                placeholder="Enter your email or phone number"
                                className="form-input"
                            />
                        </Form.Group>
                        <Form.Group controlId="password" className="form-group">
                            <Form.ControlLabel className="form-label">
                                <LockIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                                Password <span className="required">*</span>
                            </Form.ControlLabel>
                            <div className="password-input-container">
                                <Form.Control
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(value: string) => setPassword(value)}
                                    placeholder="Enter your password"
                                    className="form-input"
                                />
                                <div
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '12px',
                                        transform: 'translateY(-50%)',
                                        cursor: 'pointer',
                                        color: '#999',
                                    }}
                                >
                                    {showPassword ? (
                                        <VisibilityOffIcon fontSize="small" />
                                    ) : (
                                        <VisibilityIcon fontSize="small" />
                                    )}
                                </div>
                            </div>
                        </Form.Group>
                        <Button
                            appearance="primary"
                            onClick={handleSubmit}
                            disabled={isLoading}
                            className="login-button"
                        >
                            {isLoading ? 'Logging In...' : 'Login'}
                        </Button>
                    </Form>
                    <div className="terms-text">
                        By clicking you agree to our <a href="#" className="terms-link">Terms Of Service and Privacy Policy</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;